/**
 * RuleConditionBuilder — Reusable condition group builder
 * Renders condition groups with AND/OR logic, field selector, operator selector, value inputs.
 * Spec: Section 8.2, 8.4, 8.5
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ConditionGroup, Condition, RuleOperator, ValueType, FieldCatalogueEntry } from '../../types/rulesEngine';

interface Props {
  conditionGroups: ConditionGroup[];
  fieldCatalogue: FieldCatalogueEntry[];
  onChange: (groups: ConditionGroup[]) => void;
}

let groupCounter = 0;
let conditionCounter = 0;
const nextGroupId = () => `grp_${++groupCounter}_${Date.now()}`;
const nextConditionId = () => `cnd_${++conditionCounter}_${Date.now()}`;

/** Operators that need no value input */
const NO_VALUE_OPERATORS: RuleOperator[] = ['is_empty', 'is_not_empty'];

/** Operators that need a "from" and "to" */
const BETWEEN_OPERATORS: RuleOperator[] = ['between'];

/** Operators that need an array of values */
const MULTI_VALUE_OPERATORS: RuleOperator[] = ['in', 'not_in'];

const RuleConditionBuilder: React.FC<Props> = ({ conditionGroups, fieldCatalogue, onChange }) => {
  const { t } = useTranslation();

  /** Locale-aware operator labels */
  const OPERATOR_LABELS: Record<RuleOperator, string> = {
    equals: t('rules.operatorEquals', 'equals'),
    not_equals: t('rules.operatorNotEquals', 'not equals'),
    in: t('rules.operatorIn', 'in'),
    not_in: t('rules.operatorNotIn', 'not in'),
    greater_than: t('rules.operatorGreaterThan', 'greater than'),
    less_than: t('rules.operatorLessThan', 'less than'),
    greater_than_or_equal: t('rules.operatorGreaterOrEqual', 'greater or equal'),
    less_than_or_equal: t('rules.operatorLessOrEqual', 'less or equal'),
    between: t('rules.operatorBetween', 'between'),
    contains: t('rules.operatorContains', 'contains'),
    not_contains: t('rules.operatorNotContains', 'not contains'),
    is_empty: t('rules.operatorIsEmpty', 'is empty'),
    is_not_empty: t('rules.operatorIsNotEmpty', 'is not empty'),
  };

  // ── Group operations ──

  const addGroup = () => {
    onChange([
      ...conditionGroups,
      { groupId: nextGroupId(), conditions: [] },
    ]);
  };

  const removeGroup = (groupId: string) => {
    onChange(conditionGroups.filter(g => g.groupId !== groupId));
  };

  const updateGroup = (groupId: string, conditions: Condition[]) => {
    onChange(conditionGroups.map(g => g.groupId === groupId ? { ...g, conditions } : g));
  };

  // ── Condition operations ──

  const addCondition = (groupId: string) => {
    const group = conditionGroups.find(g => g.groupId === groupId);
    if (!group) return;
    const defaultField = fieldCatalogue[0];
    const newCondition: Condition = {
      conditionId: nextConditionId(),
      field: defaultField?.field || '',
      operator: defaultField?.operators[0] || 'equals',
      value: undefined,
      valueType: defaultField?.valueType || 'string',
    };
    updateGroup(groupId, [...group.conditions, newCondition]);
  };

  const updateCondition = (groupId: string, conditionId: string, updates: Partial<Condition>) => {
    const group = conditionGroups.find(g => g.groupId === groupId);
    if (!group) return;
    updateGroup(
      groupId,
      group.conditions.map(c => c.conditionId === conditionId ? { ...c, ...updates } : c)
    );
  };

  const removeCondition = (groupId: string, conditionId: string) => {
    const group = conditionGroups.find(g => g.groupId === groupId);
    if (!group) return;
    updateGroup(groupId, group.conditions.filter(c => c.conditionId !== conditionId));
  };

  // When field changes, reset operator and value to match the new field type
  const handleFieldChange = (groupId: string, conditionId: string, fieldPath: string) => {
    const catalogueEntry = fieldCatalogue.find(f => f.field === fieldPath);
    if (!catalogueEntry) return;
    updateCondition(groupId, conditionId, {
      field: fieldPath,
      valueType: catalogueEntry.valueType,
      operator: catalogueEntry.operators[0],
      value: undefined,
    });
  };

  // When operator changes, reset value if the input type changes
  const handleOperatorChange = (groupId: string, conditionId: string, operator: RuleOperator) => {
    const updates: Partial<Condition> = { operator };
    if (NO_VALUE_OPERATORS.includes(operator)) {
      updates.value = undefined;
    } else if (BETWEEN_OPERATORS.includes(operator)) {
      updates.value = { from: '', to: '' };
    } else if (MULTI_VALUE_OPERATORS.includes(operator)) {
      updates.value = [];
    } else {
      updates.value = '';
    }
    updateCondition(groupId, conditionId, updates);
  };

  // ── Value input rendering ──

  const renderValueInput = (groupId: string, condition: Condition, catalogueEntry?: FieldCatalogueEntry) => {
    if (NO_VALUE_OPERATORS.includes(condition.operator)) {
      return null;
    }

    if (BETWEEN_OPERATORS.includes(condition.operator)) {
      const val = (condition.value as { from?: string; to?: string }) || { from: '', to: '' };
      const inputType = condition.valueType === 'number' ? 'number' : condition.valueType === 'date' ? 'date' : 'text';
      return (
        <div className="between-inputs">
          <input
            type={inputType}
            className="condition-value-input"
            placeholder={t('rules.valueFrom', 'From')}
            value={val.from || ''}
            onChange={e => updateCondition(groupId, condition.conditionId, {
              value: { ...val, from: e.target.value }
            })}
          />
          <span className="between-separator">–</span>
          <input
            type={inputType}
            className="condition-value-input"
            placeholder={t('rules.valueTo', 'To')}
            value={val.to || ''}
            onChange={e => updateCondition(groupId, condition.conditionId, {
              value: { ...val, to: e.target.value }
            })}
          />
        </div>
      );
    }

    if (MULTI_VALUE_OPERATORS.includes(condition.operator)) {
      const values = Array.isArray(condition.value) ? condition.value as string[] : [];
      return (
        <MultiValueInput
          values={values}
          options={catalogueEntry?.options}
          onChange={newValues => updateCondition(groupId, condition.conditionId, { value: newValues })}
        />
      );
    }

    // Single value — dropdown, number, date, or text
    if (catalogueEntry?.options && catalogueEntry.options.length > 0) {
      return (
        <select
          className="condition-value-input"
          value={String(condition.value || '')}
          onChange={e => updateCondition(groupId, condition.conditionId, { value: e.target.value })}
        >
          <option value="">{t('rules.selectValue', '-- Select --')}</option>
          {catalogueEntry.options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }

    if (condition.valueType === 'number') {
      return (
        <input
          type="number"
          className="condition-value-input"
          placeholder={t('rules.value', 'Value')}
          value={condition.value !== undefined ? String(condition.value) : ''}
          onChange={e => updateCondition(groupId, condition.conditionId, { value: e.target.value })}
        />
      );
    }

    if (condition.valueType === 'date') {
      return (
        <input
          type="date"
          className="condition-value-input"
          value={dateToInputValue(condition.value as string)}
          onChange={e => updateCondition(groupId, condition.conditionId, { value: inputValueToDate(e.target.value) })}
        />
      );
    }

    return (
      <input
        type="text"
        className="condition-value-input"
        placeholder={t('rules.value', 'Value')}
        value={String(condition.value || '')}
        onChange={e => updateCondition(groupId, condition.conditionId, { value: e.target.value })}
      />
    );
  };

  // ── Render ──

  return (
    <div className="rule-condition-builder">
      {conditionGroups.length === 0 && (
        <div className="catch-all-notice">
          {t('rules.catchAllNotice', 'No conditions — this rule will match all trades (catch-all).')}
        </div>
      )}

      {conditionGroups.length > 0 && (
        <div className="condition-groups-row">
          {conditionGroups.map((group, groupIndex) => (
            <React.Fragment key={group.groupId}>
              {groupIndex > 0 && <div className="group-or-separator">OR</div>}
              <div className="condition-group">
                <div className="condition-group-header">
                  <span className="group-label">
                    {t('rules.groupLabel', 'Group')} {groupIndex + 1}
                  </span>
                  <span className="group-hint">
                    {t('rules.allMustMatch', 'AND')}
                  </span>
                  <button
                    type="button"
                    className="remove-group-btn"
                    onClick={() => removeGroup(group.groupId)}
                    title={t('rules.removeGroup', 'Remove group')}
                  >
                    ×
                  </button>
                </div>

                <div className="conditions-list">
                  {group.conditions.map(condition => {
                    const catalogueEntry = fieldCatalogue.find(f => f.field === condition.field);
                    const availableOperators = catalogueEntry?.operators || [];
                    return (
                      <div key={condition.conditionId} className="condition-row">
                        <select
                          className="condition-field-select"
                          value={condition.field}
                          onChange={e => handleFieldChange(group.groupId, condition.conditionId, e.target.value)}
                        >
                          <option value="">{t('rules.selectField', '-- Field --')}</option>
                          {fieldCatalogue.map(f => (
                            <option key={f.field} value={f.field}>{f.displayName}</option>
                          ))}
                        </select>

                        <select
                          className="condition-operator-select"
                          value={condition.operator}
                          onChange={e => handleOperatorChange(group.groupId, condition.conditionId, e.target.value as RuleOperator)}
                        >
                          {availableOperators.map(op => (
                            <option key={op} value={op}>{OPERATOR_LABELS[op] || op}</option>
                          ))}
                        </select>

                        {renderValueInput(group.groupId, condition, catalogueEntry)}

                        <button
                          type="button"
                          className="remove-condition-btn"
                          onClick={() => removeCondition(group.groupId, condition.conditionId)}
                          title={t('rules.removeCondition', 'Remove condition')}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  className="add-condition-btn"
                  onClick={() => addCondition(group.groupId)}
                >
                  + {t('rules.addCondition', 'Add Condition')}
                </button>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      <button
        type="button"
        className="add-group-btn"
        onClick={addGroup}
      >
        + {t('rules.addGroup', 'Add Condition Group')}
      </button>
    </div>
  );
};

// ── Multi-value input (for in / not_in operators) ──

interface MultiValueInputProps {
  values: string[];
  options?: { label: string; value: string }[];
  onChange: (values: string[]) => void;
}

const MultiValueInput: React.FC<MultiValueInputProps> = ({ values, options, onChange }) => {
  const [inputValue, setInputValue] = React.useState('');

  const addValue = (val: string) => {
    const trimmed = val.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setInputValue('');
  };

  const removeValue = (val: string) => {
    onChange(values.filter(v => v !== val));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addValue(inputValue);
    }
  };

  return (
    <div className="multi-value-input">
      <div className="multi-value-tags">
        {values.map(v => (
          <span key={v} className="multi-value-tag">
            {options?.find(o => o.value === v)?.label || v}
            <button type="button" onClick={() => removeValue(v)}>×</button>
          </span>
        ))}
      </div>
      {options && options.length > 0 ? (
        <select
          className="condition-value-input"
          value=""
          onChange={e => { if (e.target.value) addValue(e.target.value); }}
        >
          <option value="">+ Add...</option>
          {options.filter(o => !values.includes(o.value)).map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          className="multi-value-text-input"
          placeholder="Type and press Enter"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (inputValue.trim()) addValue(inputValue); }}
        />
      )}
    </div>
  );
};

// ── Date helpers (DD-MM-YYYY ↔ YYYY-MM-DD for <input type="date">) ──

function dateToInputValue(ddmmyyyy: string | undefined): string {
  if (!ddmmyyyy || typeof ddmmyyyy !== 'string') return '';
  const parts = ddmmyyyy.split('-');
  if (parts.length !== 3) return '';
  return `${parts[2]}-${parts[1]}-${parts[0]}`;  // YYYY-MM-DD
}

function inputValueToDate(yyyymmdd: string): string {
  if (!yyyymmdd) return '';
  const parts = yyyymmdd.split('-');
  if (parts.length !== 3) return '';
  return `${parts[2]}-${parts[1]}-${parts[0]}`;  // DD-MM-YYYY
}

export default RuleConditionBuilder;
